import { useState } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState, SectionHeading } from '@ets/design-system';
import { useParams } from 'react-router';

import { addUserToGroup, removeUserFromGroup } from 'App/redux/appSlice';
import { useDispatch, usePublicGroup } from '_common/hooks';
import { SearchUser } from '_common/components';
import { UserOption } from '_common/components/SearchUser/SearchUser';

import Table, { type TableProps } from '../Table/Table';

const MembersView = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id: groupId }: { id: ObjectId } = useParams();

  const { group } = usePublicGroup(groupId);

  const [memberSelected, setMemberSelected] = useState<UserOption | null>();
  const useIsOwner =
    group.user_permissions.includes('admin') || group.user_permissions.includes('owner');

  const handleOnClickAddMember = () => {
    if (memberSelected) {
      dispatch(addUserToGroup({ groupId: group.id, userId: memberSelected.value }));
      setMemberSelected(undefined);
    }
  };

  const handleRemoveUser: TableProps['removeUserHandler'] = (user) => {
    dispatch(removeUserFromGroup({ groupId, userId: user.id }));
  };

  return (
    <>
      {/* Members */}
      <SectionHeading
        title={intl.formatMessage({ id: 'settings.group.groupMembersTitle' })}
        searchBar={
          useIsOwner ? (
            <SearchUser
              value={memberSelected}
              valuesToFilter={group.users}
              onChange={(user) => setMemberSelected(user as UserOption | null)}
              placeholder={intl.formatMessage({
                id: 'settings.group.placeholderAutocomplete',
              })}
              escapeClearsValue
              width="37rem"
              testId="members"
              size="m"
              menuLabel={intl.formatMessage({ id: 'START_TYPING_TO_SEE_THE_LIST_OF_USERS' })}
            />
          ) : null
        }
        firstAction={
          useIsOwner
            ? {
                children: intl.formatMessage({ id: 'global.add' }),
                variant: 'primary',
                onClick: handleOnClickAddMember,
                disabled: !memberSelected,
              }
            : undefined
        }
        testId="groups-members"
        size="l"
        style={{ margin: '2rem 0' }}
      />

      {group.users.length === 0 ? (
        <EmptyState
          description=""
          style={{ margin: '2rem 0 0 0' }}
          size="m"
          title={intl.formatMessage({ id: 'groups.emptyList' })}
          testId="group-settings-page-no-members"
        />
      ) : (
        <Table
          identity={'groups_members'}
          group={group}
          users={group.users}
          removeUserHandler={handleRemoveUser}
          testId="members-tab"
        />
      )}
    </>
  );
};

export default MembersView;
